import _ from "lodash"
import { DEFAULT_FEATURE_QUICK_SEARCH_ORDER_CONFIG } from "hooks/feature/useFeatureQuickSearch/constant"
import dayjs from "config/dayjs"
import {
  DurationUnitsObjectType,
  IAppConfig,
  IDeviceConfig,
  IExtrudeConfig,
  IIdleModeConfig,
  IMapConfig,
  IMapExtrudeConfig,
  ISearchConfig,
  IStepConfig,
  IVenueControllerConfig,
  PageConfigs,
} from "services/venue"
import {
  set as setPersistValue,
  remove as removePersistValue,
} from "utils/persist_store"
import { FORCE_VENUE_ID_KEY } from "constant"

const prepareExtrudeConfig = (extrudeConfig: {
  unit: IMapExtrudeConfig[]
  kiosk: IMapExtrudeConfig[]
}): IExtrudeConfig => {
  if (!extrudeConfig) return

  const defaultExtrudeConfig: IExtrudeConfig = {
    unit: [],
    kiosk: [],
  }

  return _.reduce(
    extrudeConfig,
    (preparedExtrudeConfig, configArr, featureType) => {
      preparedExtrudeConfig[featureType] = _.reduce(
        configArr,
        (featureTypeConfig, configItem) => {
          const category = _.get(configItem, "category")
          const height = _.get(configItem, "height")

          if (category && height) _.set(featureTypeConfig, category, { height })

          return featureTypeConfig
        },
        {}
      )
      return preparedExtrudeConfig
    },
    defaultExtrudeConfig
  )
}

export const prepareMapConfig = (mapConfig: IMapConfig): IMapConfig => {
  if (!mapConfig) return
  const DEFAULT_MAP_CONFIG = {
    shortest_path: {
      resolution: 0.0015,
    },
  }
  const extrudeConfig = prepareExtrudeConfig(mapConfig.extrude)
  return _.merge({}, DEFAULT_MAP_CONFIG, {
    ...mapConfig,
    extrude: extrudeConfig,
  })
}

export const getDeviceConfigKey = (
  appConfig: IAppConfig,
  deviceType: string
): string => {
  // Check if kiosk-h, kiosk-v, or mobile exist; return the key, otherwise return kiosk as a fallback key.
  return _.get(appConfig, deviceType) ? deviceType : "kiosk"
}

export const prepareStepAutoplayConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IStepConfig => {
  if (!appConfig) return
  const DEFAULT_AUTOPLAY_CONFIG = { delay: 3000, enable: false }
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  const stepConfig = _.get(appConfig, `${deviceConfigKey}.autoplay.step`, {})
  return _.merge({}, DEFAULT_AUTOPLAY_CONFIG, stepConfig)
}

export const prepareIdleModeConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IIdleModeConfig => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  const isKiosk = `${deviceConfigKey}`.startsWith("kiosk")

  const DEFAULT_IDLE_CONFIG = { redirectPath: "/", enable: isKiosk }
  const fallbackThrottleMs: DurationUnitsObjectType["milliseconds"] = dayjs
    .duration({ seconds: 0.5 })
    .asMilliseconds()
  const fallbackTimeoutMs: DurationUnitsObjectType["milliseconds"] = dayjs
    .duration({ minutes: 5 })
    .asMilliseconds()

  /**
   * NOTE: `throttle` and `timeout` configurations support the same format as the duration function in Day.js.
   * @ref: https://day.js.org/docs/en/durations/durations
   */
  const newIdleConfig: IIdleModeConfig = _.get(
    appConfig,
    `${deviceConfigKey}.idleMode`,
    {}
  )
  const {
    redirectPath,
    enable,
    throttle: newThrottle,
    timeout: newTimeout,
  } = _.merge({}, DEFAULT_IDLE_CONFIG, newIdleConfig)

  // Convert throttle and timeout configurations to milliseconds using Day.js.
  const throttleMs: DurationUnitsObjectType["milliseconds"] = dayjs
    .duration(newThrottle)
    .asMilliseconds()
  const timeoutMs: DurationUnitsObjectType["milliseconds"] = dayjs
    .duration(newTimeout)
    .asMilliseconds()

  return {
    redirectPath,
    disabled: !enable,
    throttle: throttleMs || fallbackThrottleMs,
    timeout: timeoutMs || fallbackTimeoutMs,
  }
}

export const prepareVenueControllerConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IVenueControllerConfig => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)

  const DEFAULT_CONFIG = {
    forceVenueId: null,
  }

  const venueControllerConfig: IVenueControllerConfig = _.get(
    appConfig,
    `${deviceConfigKey}.venueController`,
    {}
  )
  return _.merge({}, DEFAULT_CONFIG, venueControllerConfig)
}

export const setupVenueControllerConfig = (
  config: IVenueControllerConfig = {}
) => {
  const { forceVenueId } = config
  forceVenueId
    ? setPersistValue(FORCE_VENUE_ID_KEY, forceVenueId)
    : removePersistValue(FORCE_VENUE_ID_KEY)
}

export const prepareSponsoredContentConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IDeviceConfig["sponsor"] => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  const DEFAULT_SPONSORED_CONTENT_CONFIG = { limit: null, disable: false }
  const sponcerConfig = _.get(appConfig, `${deviceConfigKey}.sponsor`, {})
  const preparedConfig = _.mapValues(sponcerConfig, (config) =>
    _.merge({}, DEFAULT_SPONSORED_CONTENT_CONFIG, config)
  )
  return preparedConfig
}

export const prepareSearchConfig = (
  appConfig: IAppConfig,
  deviceType: string
): ISearchConfig => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  const defaultSearchConfig: ISearchConfig = { featureTypes: [], keys: [] }
  return _.get(appConfig, `${deviceConfigKey}.search`, defaultSearchConfig)
}

export const getIndexPathConfig = (
  appConfig: IAppConfig,
  deviceType: string
): ISearchConfig => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  return _.get(appConfig, `${deviceConfigKey}.navigation.index`)
}

export const getDefaultRoutePathConfig = (
  appConfig: IAppConfig,
  deviceType: string
) => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  return _.get(
    appConfig,
    `${deviceConfigKey}.navigation.defaultRoutePath`,
    "/maps"
  )
}

export const prepareExtensionConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IDeviceConfig["extensions"] => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  return _.get(appConfig, `${deviceConfigKey}.extensions`, {})
}

export const prepareFeatureQuickSearchConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IDeviceConfig["featureQuickSearch"] => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  return _.get(
    appConfig,
    `${deviceConfigKey}.featureQuickSearch`,
    DEFAULT_FEATURE_QUICK_SEARCH_ORDER_CONFIG
  )
}

/*
 ==== Example Config ====
  "venueFeatureQuickSearch": {
    "parade": [
      "amenity.information",
       ...Add more features as needed...
    ],
    "the-storeys": [
      "amenity.information",
      "amenity.restroom",
     ..Add more features as needed...
    ]
  }
*/
export const prepareVenueFeatureQuickSearchConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IDeviceConfig["venueFeatureQuickSearch"] => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  return _.get(appConfig, `${deviceConfigKey}.venueFeatureQuickSearch`, {})
}

export const prepareKeyboardConfig = (
  appConfig: IAppConfig,
  deviceType: string
): IDeviceConfig["keyboard"] => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  return _.get(appConfig, `${deviceConfigKey}.keyboard`, {
    languages: ["en", "th"],
  })
}

const DEFAULT_PAGE_CONFIG = {
  newShop: { metadata: { periodInDays: 30 } },
  promotion: { metadata: { filter: { category: "store" } } },
  event: { metadata: { displayMode: "all" } },
  directory: { metadata: { multipleLocations: false, periodInDays: 30 } },
}
export const preparePageConfig = (
  appConfig: IAppConfig,
  deviceType: string
): PageConfigs => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)

  const pageConfig = _.get(appConfig, `${deviceConfigKey}.pages`, {})
  return _.merge({}, DEFAULT_PAGE_CONFIG, pageConfig) as PageConfigs
}
